import React from 'react';
import { observer } from 'mobx-react-lite';
import { makeStyles } from '@material-ui/core/styles';
import { Grid, Typography, Avatar, Badge, Box } from '@material-ui/core';
import { get, isEmpty } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import classNames from 'classnames';

import config from '@common/config';
import useResponsiveness from '@frontend/hooks/useResponsiveness';

const useStyles = makeStyles(() => ({
  nowrap: {
    flexWrap: 'nowrap',
    height: '50%',
  },
  subMenuContainer: {
    zIndex: 10,
    position: 'absolute',
    left: 0,
    top: '2.5rem',
    width: 'auto',
    minWidth: 125,
    paddingTop: '20px',

    '@media (max-width: 600px)': {
      width: '125px',
      left: 'unset',
      right: '12px',
    },
  },
  subMenu: {
    height: 'max-content',
    backgroundColor: '#FFFFFF',
    padding: '0.75rem',
    boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.2)',
    position: 'relative',
    width: 'calc(100% + 20px)',

    '&::before': {
      content: '""',
      position: 'absolute',
      top: '-0.625rem',
      left: '1.4375rem',
      marginLeft: -5,
      borderWidth: 6,
      borderStyle: 'solid',
      borderColor: 'transparent transparent #FFF transparent',

      '@media (max-width: 600px)': {
        left: '120px',
      },
    },
  },
  avatar: {
    width: '24px',
    height: '24px',
    marginRight: 5,
  },
  menuItem: {
    color: 'white',
    fontSize: '1rem',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  link: {
    fontSize: '16px',
    color: 'black',
  },
  profileOutlined: {
    border: '1px solid rgba(255, 255, 255, 0.4)',
    borderRadius: 5,
    padding: 10,
  },
  menuItemIcon: {
    color: '#FFFFFF',
    fontSize: '1.25rem',
  },
  outlined: {
    border: '1px solid rgba(255, 255, 255, 0.4)',
    borderRadius: 5,
    padding: 10,
  },
  unreadNotifications: {
    backgroundColor: '#FFFFFF',
    '& i': {
      color: 'red',
    },
    '& svg': {
      color: 'red',
    },
  },
  small: {
    width: '24px',
    height: '24px',
  },
  profileGrid: {
    position: 'relative',
    width: 'auto',
    margin: 'auto',
    cursor: 'pointer',

    '&:hover': {
      backgroundColor: (isGenAI) => (isGenAI ? '#ffffff38' : '#494949'),
    },
  },
}));

const UserInfo = observer(({
  user,
  lang,
  handleAdminLogin,
  handleChangeInstance,
  handleAcademyLogin,
  goToLink,
  hoverKey,
  toggleHoverOn,
  toggleHoverOff,
  isAdmin,
  isCoach,
  firstName,
  totalNotificationsInboxCount,
  goToLinkMarkRead,
  hasNewCertificationFeedback,
  isGenAI,
}) => {
  const classes = useStyles({ isGenAI });
  const { isMobile } = useResponsiveness();

  const { multipleInstances } = user;
  const hasMultipleInstances = multipleInstances?.length > 1;

  const avatar = get(user, 'preferences.avatar');
  const unreadNotificationHeaders = [];

  if (hasNewCertificationFeedback) {
    unreadNotificationHeaders.push({
      display: 'New Certification Feedback',
      link: '/certify?ref=button',
    });
  }

  return (
    <Grid item>
      <Grid container direction="row" className={classes.nowrap}>
        <Grid
          item
          className={classes.profileGrid}
        >
          <Box
            onMouseEnter={!!totalNotificationsInboxCount
              && (() => toggleHoverOn('notifications'))}
            onMouseLeave={toggleHoverOff}
            className={classNames(
              classes.outlined,
              { [classes.unreadNotifications]: !!totalNotificationsInboxCount },
            )}
            onClick={() => goToLink(isCoach || isAdmin ? '/inbox' : '/path')}
          >
            {totalNotificationsInboxCount ? (
              <Badge
                badgeContent={totalNotificationsInboxCount}
                max={999}
                color="error"
              >
                <FontAwesomeIcon
                  icon={['far', 'bell']}
                  className={classNames(classes.menuItemIcon)}
                />
              </Badge>
            ) : (
              <FontAwesomeIcon
                icon={['far', 'bell']}
                className={classNames(classes.menuItemIcon)}
              />
            )}
          </Box>
          {unreadNotificationHeaders.length > 0
            && hoverKey === 'notifications'
            && (
              <Grid
                container
                item
                className={classes.subMenuGridWide}
                spacing={2}
                onMouseEnter={() => toggleHoverOn('notifications')}
                onMouseLeave={toggleHoverOff}
              >
                {unreadNotificationHeaders.map((subItem, index) => (
                  <Grid item xs={12} key={index}>
                    <Typography
                      className={classes.link}
                      onClick={() => goToLinkMarkRead(subItem.link)}
                    >
                      {subItem.display}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            )}
        </Grid>
        <Grid
          container
          item
          onMouseEnter={() => toggleHoverOn('profile')}
          onMouseLeave={toggleHoverOff}
          alignItems="center"
          style={{ marginLeft: 15 }}
          className={classNames(
            classes.profileOutlined,
            classes.profileGrid,
            classes.outlined,
          )}
        >
          <Grid item>
            <Grid
              container
              direction="row"
              alignItems="center"
              justify="center"
              className={classes.nowrap}
            >
              {!isEmpty(avatar) && (
                <Grid item>
                  <Avatar
                    style={{ marginRight: 5 }}
                    alt={`${firstName}`}
                    src={avatar}
                    className={classes.small}
                  />
                </Grid>
              )}
              {!isEmpty(firstName)
                && (!isMobile || !isGenAI)
                && (
                  <Grid item>
                    <Typography className={classes.menuItem}>
                      &nbsp;
                      {firstName}
                    </Typography>
                  </Grid>
                )}
            </Grid>
            {hoverKey === 'profile' && (
              <div className={classes.subMenuContainer}>
                <Grid
                  container
                  item
                  className={classes.subMenu}
                  spacing={2}
                >
                  {(isCoach || isAdmin) && (
                    <Grid item xs={12}>
                      <a
                        style={{
                          color: 'black',
                          textDecoration: 'none',
                        }}
                        href="https://20564515.hs-sites.com/knowledge"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Typography
                          className={classes.link}
                          style={{
                            color: 'black',
                            textDecoration: 'none',
                          }}
                        >
                          Help Center
                        </Typography>
                      </a>
                    </Grid>
                  )}
                  {isAdmin && (
                    <Grid item xs={12}>
                      <Typography
                        className={classes.link}
                        onClick={() => handleAdminLogin()}
                      >
                        Admin
                      </Typography>
                    </Grid>
                  )}
                  {(isAdmin && hasMultipleInstances) && (
                    <Grid item xs={12}>
                      <Grid
                        style={{
                          color: 'black',
                          textDecoration: 'none',
                        }}
                        onClick={handleChangeInstance}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Typography
                          className={classes.link}
                          style={{
                            color: 'black',
                            textDecoration: 'none',
                          }}
                        >
                          Switch Instance
                        </Typography>
                      </Grid>
                    </Grid>
                  )}
                  {[
                    {
                      display: 'Profile',
                      onClick: () => goToLink('/profile'),
                    },
                    {
                      display: 'Academy',
                      onClick: () => handleAcademyLogin(),
                    },
                    {
                      display: lang.t('header.logout'),
                      onClick: () => goToLink('/logout'),
                    },
                  ].map((subItem, index) => (
                    (!config.isAcademy && isAdmin) || index !== 1)
                    && (
                      <Grid item xs={12} key={index}>
                        <Typography
                          className={classes.link}
                          onClick={subItem.onClick}
                        >
                          {subItem.display}
                        </Typography>
                      </Grid>
                    ),
                  )}
                </Grid>
              </div>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
});

export default UserInfo;
